import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Card, Col, Empty, Modal, Row, message, Spin } from "antd";
import BannerCard from "./BannerCard";
import { reorderBanners } from "../../api/campaignsApi";

const BannerList = ({ campaignId, banners, onBannerDeleted, onBannersReordered }) => {
  const { t } = useTranslation();
  const [reordering, setReordering] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);

  const handleDelete = (bannerId) => {
    Modal.confirm({
      title: t("campaigns.deleteBannerConfirmTitle"),
      content: t("campaigns.deleteBannerConfirmContent"),
      okText: t("common.confirmAnswer"),
      okType: "danger",
      cancelText: t("common.rejectAnswer"),
      onOk: async () => {
        try {
          const { deleteBanner } = await import("../../api/campaignsApi");
          await deleteBanner(bannerId);
          message.success(t("campaigns.bannerDeleteSuccess"));
          onBannerDeleted();
        } catch (error) {
          console.error(error);
          message.error(t("campaigns.bannerDeleteError"));
        }
      },
    });
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/html", e.currentTarget);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const handleDrop = async (e, dropIndex) => {
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      return;
    }

    setReordering(true);
    try {
      // Create new order array
      const reorderedBanners = [...banners];
      const [draggedBanner] = reorderedBanners.splice(draggedIndex, 1);
      reorderedBanners.splice(dropIndex, 0, draggedBanner);

      // Send only banner IDs in the new order
      const bannerIds = reorderedBanners.map(b => b.id);
      
      await reorderBanners(campaignId, bannerIds);
      message.success(t("campaigns.bannersReordered"));
      onBannersReordered();
    } catch (error) {
      console.error(error);
      message.error(error.response?.data?.message || t("campaigns.reorderError"));
    } finally {
      setReordering(false);
      setDraggedIndex(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  if (!banners || banners.length === 0) {
    return (
      <Card>
        <Empty
          description={t("campaigns.noBanners")}
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      </Card>
    );
  }

  return (
    <Card 
      title={t("campaigns.bannersList")}
      extra={reordering && <Spin size="small" />}
    >
      <Row gutter={[16, 16]}>
        {banners.map((banner, index) => (
          <Col 
            key={banner.id} 
            xs={24} 
            sm={12} 
            md={8} 
            lg={6}
            draggable
            onDragStart={(e) => handleDragStart(e, index)}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, index)}
            onDragEnd={handleDragEnd}
            style={{
              cursor: reordering ? "wait" : "move",
              opacity: draggedIndex === index ? 0.5 : 1,
              transition: "opacity 0.2s",
            }}
          >
            <BannerCard
              banner={banner}
              onDelete={handleDelete}
            />
          </Col>
        ))}
      </Row>
    </Card>
  );
};

export default BannerList;
